<script setup lang="ts">
/**
 * "Type" column cell for the client card visits table. Renders one
 * transaction label per row with kind-specific typography:
 *
 * - `lesson`  – italic serif (matches Tufte body), reading as descriptive
 *   text for the service-type name. Optionally supports a second line
 *   (`subtitle`, e.g. duration `1h 30m`) rendered muted and smaller, so
 *   the cell can replace the previous one-line `<name> <duration>` form
 *   that crowded the column on long descriptions.
 * - `payment` – sans-serif uppercase tracking, mirroring the mock's
 *   "PAYMENT" badge so it stands out from the surrounding lesson rows.
 *   Subtitles are ignored on payment rows because the badge is meant to
 *   stay a single visual unit.
 *
 * Wrapping policy: the primary label wraps freely onto additional lines
 * when it is wider than the column, with each row growing in height to
 * accommodate the wrapped text. The subtitle stays on a single line
 * underneath because durations are short by nature; long subtitles
 * (which we do not currently expect) would clip with `…` instead of
 * pushing the row even taller. The native `title` attribute exposes the
 * full untruncated label + subtitle so it remains discoverable on hover.
 */
const props = withDefaults(
  defineProps<{
    /** Primary line. Service-type name for lessons, `Payment` for payments. */
    label: string
    /**
     * Optional secondary line shown below `label` on lesson rows only,
     * styled as muted small text (mirrors the lower line of
     * `TwoLineTextCell` / `DateTimeCell`). Pass the formatted duration here
     * (e.g. `1h 30m`) so the column reads as `name` over `duration`. Empty
     * / null / undefined collapses the cell back to a single line.
     */
    subtitle?: string | null
    /**
     * Discriminator from `MergedRow.kind`. Only `lesson` and `payment` are
     * meaningful; the cell tolerates other values by falling back to the
     * lesson styling.
     */
    kind: 'lesson' | 'payment'
    /** Forwarded from `DynamicTable` column config; caps cell width when set. */
    maxWidth?: string
  }>(),
  {
    subtitle: null,
    maxWidth: undefined,
  },
)

function nonEmpty(v: string | null | undefined): string | null {
  if (v == null) return null
  const t = v.trim()
  return t === '' ? null : t
}
</script>

<template>
  <td
    class="transaction-label-cell font-tufte"
    :class="`transaction-label-cell--${props.kind}`"
    :style="props.maxWidth ? { maxWidth: props.maxWidth } : undefined"
    :title="
      nonEmpty(props.label) && nonEmpty(props.subtitle)
        ? `${props.label}\n${props.subtitle}`
        : (nonEmpty(props.label) ?? undefined)
    "
  >
    <template v-if="props.kind === 'lesson' && nonEmpty(props.subtitle)">
      <div class="transaction-label-cell__stack">
        <span class="transaction-label-cell__title">{{ props.label }}</span>
        <span class="transaction-label-cell__subtitle">{{ props.subtitle }}</span>
      </div>
    </template>
    <template v-else>{{ props.label }}</template>
  </td>
</template>

<style scoped>
.transaction-label-cell {
  /* Defaults match the mock's `td-label` baseline (italic serif). The
   * Tufte body wraps freely on whitespace and `overflow-wrap: anywhere`
   * lets long unbroken tokens (Cyrillic with underscores, ids) break to
   * the next line so they never push the column out. `vertical-align:
   * top` aligns the wrapped paragraph with the date column above the
   * row, mirroring the alignment users expect from a multi-line cell.
   * `font-size` matches the size now used consistently everywhere a
   * business service type's own name is shown (lesson rows' `label` is
   * that name). `--payment` below overrides back down to `xs` for its own
   * badge-style presentation, independent of this size. */
  font-style: italic;
  font-size: var(--text-tufte-lg);
  color: var(--color-tufte-text);
  white-space: normal;
  overflow-wrap: anywhere;
  vertical-align: top;
}

.transaction-label-cell--payment {
  /*
   * Payment rows use a sans-serif "tag" so they punch through the otherwise
   * uniform italic ledger; matches the mock's `.row-payment td.td-label`.
   * Smaller font + uppercase tracking keeps it visually parallel to the
   * `--color-money-positive` delta in the next column.
   */
  font-style: normal;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-money-positive);
}

.transaction-label-cell__stack {
  /* Two-line stack: the title may itself wrap onto multiple lines when
   * the service-type name is wider than the column, so we use a regular
   * block-level flex column rather than a single-line `nowrap` layout.
   * `gap` keeps the muted subtitle close to the title even when the
   * title wraps. */
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  line-height: 1.2;
}

.transaction-label-cell__title {
  /* Wraps freely; Tufte italic from the parent applies. */
  display: block;
  white-space: normal;
  overflow-wrap: anywhere;
}

.transaction-label-cell__subtitle {
  font-style: normal;
  font-family: var(--font-tufte-mono);
  font-size: calc(var(--text-tufte-xs) - 1px);
  color: var(--color-tufte-muted);
  /* Subtitles are short by nature (durations like `1h 30m`); keep them on
   * one line and clip with `…` if they ever exceed the column. We do
   * *not* propagate this rule to the title because the user explicitly
   * asked for the title to wrap onto another line. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
